import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";

const MONTHS_BM = ["Januari", "Februari", "Mac", "April", "Mei", "Jun", "Julai", "Ogos", "September", "Oktober", "November", "Disember"];

const fmtMasa = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  if (h < 12) return `${h}.${String(m).padStart(2, "0")} pg`;
  if (h === 12) return `12.${String(m).padStart(2, "0")} t/h`;
  if (h < 19) return `${h - 12}.${String(m).padStart(2, "0")} ptg`;
  return `${h - 12}.${String(m).padStart(2, "0")} mlm`;
};
const fmtTarikh = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${Number(d)}.${Number(m)}.${y.slice(2)}`;
};

const CIRI = [
  "Mempunyai tahap kesihatan yang baik.",
  "Bebas daripada pengaruh dadah dan alkohol.",
  "Sentiasa menjaga ketrampilan diri, berpakaian kemas, bersopan dan mesra pelanggan.",
  "Sentiasa menjaga disiplin waktu dan menepati masa atau berada di lokasi 10 minit lebih awal.",
  "Jujur dan ikhlas dalam menjalankan tugas serta sentiasa menanam semangat kerjasama yang tinggi.",
  "Sentiasa mengetahui jadual rasmi Ketua/ Pegawai yang dibawa serta peka terhadap sebarang perubahan jadual tersebut.",
  "Mahir dengan selok-belok jalan yang akan dilalui.",
  "Mematuhi dan melaksanakan arahan asalkan tidak bercanggah dengan undang-undang serta peraturan.",
  "Tidak melibatkan diri dengan aktiviti yang melanggar undang-undang negara.",
  "Sentiasa mematuhi undang-undang dan peraturan lalu lintas.",
  "Mengurus kenderaan di bawah jagaan dengan baik dari segi kebersihan dan keselamatan kenderaan.",
  "Memastikan penyenggaraan dan servis kenderaan dilakukan mengikut jadual yang ditetapkan.",
  "Tidak membuat penambahan/ penukaran komponen kenderaan.",
  "Melaporkan kerosakan atau kecacatan kenderaan kepada Pegawai Kenderaan atau Pegawai yang diberi kuasa.",
];

const SEBELUM = [
  "Memastikan tempoh lesen memandu masih sah.",
  "Memastikan tempoh cukai jalan masih sah.",
  "Memastikan paras bahan api, minyak pelincir, minyak brek, minyak stereng, minyak kotak gear, air radiator dan air alat kesat cermin mencukupi.",
  "Memastikan paras air bateri mencukupi dan keadaan terminal bateri ketat dan bersih.",
  "Memeriksa keadaan pendawaian (wiring).",
  "Memeriksa keadaan sistem kunci berpusat dan sistem penggera keselamatan.",
  "Memeriksa semua sistem lampu supaya berfungsi.",
  "Memastikan nombor pendaftaran kenderaan bersih dan jelas.",
  "Membersihkan ruang dalaman kenderaan, alas kaki dan cermin tingkap kenderaan.",
  "Memastikan tayar dalam keadaan baik dan tekanan angin mencukupi.",
  "Memastikan tayar ganti dan peralatan/ perkakas asas (tools) termasuk Kit Bantuan Kecemasan dan Alat Pemadam Api adalah dalam keadaan yang baik.",
  "Memastikan keadaan absorber dan suspension adalah baik.",
  "Memeriksa keadaan sistem stereng dan pastikan brek berfungsi.",
  "Menentukan sistem hawa dingin dan sistem audio ditutup sebelum enjin dihidupkan.",
  "Mengambil sedikit masa (sekurang-kurangnya 1 minit) untuk memanaskan enjin sebelum memulakan perjalanan dan memastikan kenderaan berada dalam keadaan yang baik.",
  "Memastikan pergerakan kenderaan hendaklah berdasarkan arahan Ketua Jabatan/ Pegawai Kenderaan.",
  "Memastikan pemandu dan penumpang memakai tali pinggang keledar semasa menaiki kenderaan.",
  "Bagi kenderaan barang, memastikan muatan tidak berlebihan, tidak membahayakan dan ditutup dengan kemas.",
];

const SEMASA = [
  "Memandu mengikut undang-undang dan peraturan lalu lintas.",
  "Memberi tumpuan kepada pemanduan dan tidak memandu secara merbahaya seperti memotong di laluan kiri atau memintas kenderaan lain secara merbahaya.",
  "Tidak memandu melebihi had laju yang ditetapkan.",
  "Tidak merokok di dalam kenderaan.",
  "Tidak menggunakan telefon bimbit semasa memandu.",
  "Peka dengan keadaan perjalanan enjin, kotak gear dan kelancaran kenderaan semasa perjalanan dan membuat catatan jika terdapat masalah teknikal atau jika perlu mengambil tindakan yang sesuai.",
  "Peka dengan keadaan persekitaran semasa perjalanan.",
];

const SELEPAS = [
  "Memastikan bahan api diisi penuh sebelum memulangkan kenderaan untuk disimpan.",
  "Menutup suis sistem pendingin hawa dan audio sebelum enjin dimatikan serta tarik brek tangan dengan sempurna.",
  "Mengisi butiran Buku Log dengan betul dan kemas.",
  "Membersihkan kenderaan dengan sebaik-baiknya.",
  "Menyimpan kenderaan dengan baik di tempat yang selamat.",
  "Melaporkan kecacatan atau kerosakan yang dikesan pada kenderaan sepanjang penggunaan kepada Pegawai Kenderaan.",
  "Memulangkan kunci kenderaan, kad inden dan kad Sistem Bayaran Tol Tanpa Resit serta SmartTag kepada Pegawai Kenderaan.",
  "Mendapatkan butiran perjalanan berikutnya dan pastikan jalan yang akan dilalui.",
];

const ITEM_GANTI = [
  "Minyak Pelincir (Engine Oil)", "Penapis Udara (Air Filter)", "Penapis Minyak Enjin (Oil Filter)",
  "Minyak Kotak Gear (Gear Box Oil)", "Minyak Kotak Gear Stereng (Steering Gear Box Oil)", "Bendalir Brek (Brake Fluid)",
  "Minyak Front Axle", "Minyak Rear Axle", "Tali Sawat Kipas (Fan Belt)", "Tali Sawat Pendingin Hawa",
  "Palam Pencucuh (Spark Plug)", "Bateri", "Tayar", "Timing Belt/ Chain",
];

const ROWS_PER_PAGE = 7;

function PekHeader() {
  return (
    <div className="pek-header">
      <span>Pekeliling Perbendaharaan Malaysia</span>
      <span className="bold">WP 4.1/2013 Lampiran C</span>
    </div>
  );
}

function Page({ children, landscape, footer }) {
  return (
    <div className={`sheet ${landscape ? "sheet-landscape" : ""}`}>
      <PekHeader />
      <div className="sheet-body">{children}</div>
      {footer && <div className="sheet-footer">{footer}</div>}
    </div>
  );
}

function ButirPage({ data, month, rows, isLast, summary, footer }) {
  const v = data.vehicle;
  const monthLabel = `${MONTHS_BM[Number(month.slice(5)) - 1]} ${month.slice(0, 4)}`;
  const blanks = Math.max(0, ROWS_PER_PAGE - rows.length);
  return (
    <Page landscape footer={footer}>
      <div className="butir-head">
        <div>
          <div className="butir-title">BUTIR-BUTIR PENGGUNAAN KENDERAAN</div>
          <div className="butir-sub">Bulan/ Tahun: <b>{monthLabel}</b></div>
        </div>
        <div className="butir-meta">
          <div>JENIS KENDERAAN: <b>{v.make} {v.model}</b></div>
          <div>NO. PENDAFTARAN: <b>{v.registration_number}</b></div>
          <div>BAHAGIAN/ UNIT: <b>{data.agency.bahagian || data.agency.name}</b></div>
          <div>No. Siri: <b>{data.no_siri}</b> &nbsp;ASAL</div>
        </div>
      </div>
      <table className="grid-table butir-table">
        <thead>
          <tr>
            <th rowSpan={2} style={{ width: "5%" }}>Tarikh</th>
            <th colSpan={2} style={{ width: "9%" }}>Masa</th>
            <th rowSpan={2} style={{ width: "10%" }}>Nama Pemandu</th>
            <th rowSpan={2} style={{ width: "21%" }}>Tujuan dan Destinasi Perjalanan (dari - ke)</th>
            <th rowSpan={2} style={{ width: "9%" }}>Nama dan Tandatangan Pelulus</th>
            <th rowSpan={2} style={{ width: "9%" }}>Nama dan Tandatangan Pengguna</th>
            <th rowSpan={2} style={{ width: "8%" }}>Bacaan Odometer (Km)</th>
            <th rowSpan={2} style={{ width: "7%" }}>Jarak Perjalanan/ Trip Meter (Km)</th>
            <th colSpan={2} style={{ width: "13%" }}>Pembelian Bahan Api *(Petrol/ Diesel/ Gas)</th>
            <th rowSpan={2} style={{ width: "9%" }}>Arahan Khas Pengguna Kenderaan</th>
          </tr>
          <tr>
            <th>Mulai</th>
            <th>Hingga</th>
            <th>No. Resit Pembelian &amp; RM</th>
            <th>Liter</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="center">{fmtTarikh(r.tarikh)}</td>
              <td className="center">{fmtMasa(r.masa_mula)}</td>
              <td className="center">{fmtMasa(r.masa_tamat)}</td>
              <td>{r.pemandu}</td>
              <td>{r.tujuan}</td>
              <td>{r.pelulus}</td>
              <td>{r.pengguna}</td>
              <td className="center">{r.odo_mula != null ? `${r.odo_mula}/ ${r.odo_tamat}` : ""}</td>
              <td className="center">{r.jarak || ""}</td>
              <td className="center">{r.resit_rm ? `RM ${Number(r.resit_rm).toFixed(2)}` : ""}</td>
              <td className="center">{r.liter ? `${r.liter} liter` : ""}</td>
              <td style={{ fontSize: "9px" }}>{r.arahan || ""}</td>
            </tr>
          ))}
          {Array.from({ length: blanks }).map((_, i) => (
            <tr key={`b${i}`} className="blank-row">
              {Array.from({ length: 12 }).map((__, j) => <td key={j}>&nbsp;</td>)}
            </tr>
          ))}
          {isLast && (
            <tr className="jumlah-row">
              <td colSpan={8} className="bold right">JUMLAH</td>
              <td className="center bold">{summary.jumlah_jarak}</td>
              <td className="center bold">{summary.jumlah_rm ? `RM ${summary.jumlah_rm.toFixed(2)}` : ""}</td>
              <td className="center bold">{summary.jumlah_liter ? `${summary.jumlah_liter} liter` : ""}</td>
              <td></td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="nota">* potong yang tidak berkenaan</div>
      {isLast && (
        <table className="grid-table kadar-table">
          <thead>
            <tr><th colSpan={6} className="kadar-title">KADAR PENGGUNAAN BAHAN API BULANAN</th></tr>
            <tr>
              <th style={{ width: "14%" }}>Bulan<br />(a)</th>
              <th style={{ width: "16%" }}>Jumlah Jarak Perjalanan (Km)<br />(b)</th>
              <th style={{ width: "17%" }}>Jumlah Penggunaan Bahan Api (Liter)<br />(c)</th>
              <th style={{ width: "17%" }}>Jumlah Pembelian Bahan Api (RM)<br />(d)</th>
              <th style={{ width: "16%" }}>Kadar Penggunaan Bahan Api (Km/ Liter)<br />(e)**</th>
              <th style={{ width: "20%" }}>Disahkan Oleh</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="center">{monthLabel}</td>
              <td className="center">{summary.jumlah_jarak} km</td>
              <td className="center">{summary.jumlah_liter} liter</td>
              <td className="center">RM {summary.jumlah_rm.toFixed(2)}</td>
              <td className="center">{summary.kadar_kmpl ?? "-"}</td>
              <td className="sah-cell">
                Tandatangan : ..............................<br />
                Nama : ..........................................<br />
                Jawatan : ......................................
              </td>
            </tr>
          </tbody>
        </table>
      )}
      {isLast && <div className="nota">** Formula Pengiraan, (e) = (b) / (c)</div>}
    </Page>
  );
}

export default function BukuLogPrint() {
  const { vehicleId } = useParams();
  const [params] = useSearchParams();
  const year = Number(params.get("year")) || new Date().getFullYear();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/reports/buku-log/${vehicleId}`, { params: { year } })
      .then((r) => setData(r.data))
      .catch(() => setErr("Gagal memuatkan data buku log."));
  }, [vehicleId, year]);

  const butirPages = useMemo(() => {
    if (!data) return [];
    const pages = [];
    const monthsToRender = data.months.length > 0 ? data.months : [{
      month: `${year}-01`, rows: [], jumlah_jarak: 0, jumlah_liter: 0, jumlah_rm: 0, kadar_kmpl: null,
    }];
    for (const m of monthsToRender) {
      const chunks = [];
      for (let i = 0; i < Math.max(1, Math.ceil(m.rows.length / ROWS_PER_PAGE)); i++) {
        chunks.push(m.rows.slice(i * ROWS_PER_PAGE, (i + 1) * ROWS_PER_PAGE));
      }
      chunks.forEach((rows, i) => pages.push({ month: m.month, rows, isLast: i === chunks.length - 1, summary: m }));
    }
    return pages;
  }, [data, year]);

  if (err) return <div style={{ padding: 40, fontFamily: "Arial" }}>{err}</div>;
  if (!data) return <div style={{ padding: 40, fontFamily: "Arial" }}>Menjana Buku Log Kenderaan...</div>;

  const v = data.vehicle;
  const totalPages = 9 + butirPages.length;
  let pageNo = 0;
  const ms = () => { pageNo += 1; return `M.S. ${pageNo}/${totalPages}`; };

  return (
    <div className="bukulog-root" data-testid="bukulog-print-page">
      <style>{`
        .bukulog-root { font-family: Arial, Helvetica, sans-serif; font-size: 13px; color: #000; background: #525659; }
        .sheet { width: 297mm; min-height: 208mm; background: #fff; margin: 12px auto; padding: 12mm 14mm; box-sizing: border-box; display: flex; flex-direction: column; position: relative; }
        .sheet-landscape { width: 297mm; min-height: 208mm; }
        .sheet-body { flex: 1; }
        .pek-header { display: flex; justify-content: space-between; font-style: italic; font-size: 12px; border-bottom: 1px solid #000; padding-bottom: 3px; margin-bottom: 10px; }
        .sheet-footer { text-align: right; font-size: 12px; font-style: italic; margin-top: 8px; }
        .bold { font-weight: bold; } .center { text-align: center; } .right { text-align: right; }
        h2.doc-title { text-align: center; font-size: 17px; font-weight: bold; text-decoration: underline; margin: 0 0 12px; font-family: Arial, Helvetica, sans-serif; }
        .grid-table { width: 100%; border-collapse: collapse; }
        .grid-table th, .grid-table td { border: 1px solid #000; padding: 4px 6px; vertical-align: top; font-size: 12px; }
        .grid-table th { text-align: center; font-weight: bold; vertical-align: middle; }
        ol.senarai { list-style: decimal; list-style-position: outside; padding-left: 24px; margin: 0; column-count: 2; column-gap: 40px; font-size: 13px; } ol.senarai li { margin-bottom: 8px; text-align: justify; break-inside: avoid; }
        .cover { text-align: center; display: flex; flex-direction: column; height: 100%; }
        .cover .agency-lines { text-align: left; font-size: 14px; margin-bottom: 14px; }
        .cover .crest { height: 84px; margin: 4px auto 12px; }
        .cover h1 { font-size: 32px; letter-spacing: 2px; margin: 8px 0 22px; font-family: Arial, Helvetica, sans-serif; }
        .cover .fields { text-align: left; font-size: 14px; max-width: 170mm; margin: 0 auto; line-height: 2.3; }
        .lampiran-c1 { text-align: right; font-weight: bold; margin-bottom: 4px; font-size: 13px; }
        .butir-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6px; }
        .butir-title { font-size: 15px; font-weight: bold; text-decoration: underline; }
        .butir-sub { font-size: 12px; margin-top: 4px; }
        .butir-meta { font-size: 11px; text-align: right; line-height: 1.6; }
        .butir-table th { font-size: 10px; padding: 3px 4px; }
        .butir-table td { height: 34px; font-size: 10px; padding: 3px 4px; }
        .blank-row td { height: 34px; }
        .jumlah-row td { background: #f2f2f2; }
        .nota { font-size: 10px; font-style: italic; margin: 4px 0; }
        .kadar-table { margin-top: 6px; } .kadar-table th, .kadar-table td { font-size: 10px; } .kadar-title { background: #f2f2f2; font-size: 12px; }
        .sah-cell { font-size: 9px; line-height: 2; }
        .print-btn { position: fixed; top: 14px; right: 18px; z-index: 50; background: #10056E; color: #fff; border: 0; border-radius: 8px; padding: 10px 18px; font-size: 13px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 14px rgba(0,0,0,.35); }
        @page { size: A4 landscape; margin: 0; }
        @media print {
          .bukulog-root { background: #fff; }
          .print-btn { display: none; }
          .sheet { margin: 0; page-break-after: always; min-height: 0; height: 208mm; }
        }
      `}</style>
      <button className="print-btn" onClick={() => window.print()} data-testid="bukulog-print-btn">Cetak / Simpan PDF</button>

      {/* 1. Cover — LAMPIRAN C1 */}
      <Page footer={ms()}>
        <div className="lampiran-c1">LAMPIRAN C1</div>
        <div className="cover">
          <div className="agency-lines">
            KEMENTERIAN/ JABATAN: <b>{data.agency.name.toUpperCase()}</b><br />
            BAHAGIAN/ UNIT: <b>{data.agency.bahagian || "....................................................."}</b>
          </div>
          <img className="crest" alt="Jata Negara" src="/jata-negara.png"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <h1>BUKU LOG KENDERAAN</h1>
          <div className="fields">
            TAHUN: <b>{data.year}</b><br />
            NO. SIRI: <b>{data.no_siri}</b><br />
            NO. PENDAFTARAN KENDERAAN: <b>{v.registration_number}</b><br />
            JENIS KENDERAAN: <b>{v.make} {v.model} ({v.vehicle_type})</b>
          </div>
        </div>
      </Page>

      {/* 2. Kandungan */}
      <Page footer={ms()}>
        <h2 className="doc-title">KANDUNGAN</h2>
        <table className="grid-table">
          <thead><tr><th style={{ width: "8%" }}>Bil.</th><th>PERKARA</th><th style={{ width: "18%" }}>MUKA SURAT</th></tr></thead>
          <tbody>
            {[
              ["1.", "Ciri-ciri Pemandu Berhemah dan Cemerlang", "3"],
              ["2.", "Arahan Harian Kepada Pemandu", "4 - 5"],
              ["", "2.1 Tanggungjawab Setiap Hari Sebelum Memandu", ""],
              ["", "2.2 Tanggungjawab Semasa Memandu", ""],
              ["", "2.3 Tanggungjawab Selepas Tamat Perjalanan", ""],
              ["3.", "Tanggungjawab Sekiranya Berlaku Kemalangan", "6"],
              ["4.", "Maklumat Kenderaan", "7"],
              ["5.", "Rekod Penyenggaraan Berjadual", "8"],
              ["6.", "Maklumat Mengenai Notis Kesalahan Lalu Lintas", "9"],
              ["7.", "Butir-butir Penggunaan Kenderaan", `10 - ${totalPages}`],
            ].map((r, i) => (
              <tr key={i}><td className="center">{r[0]}</td><td>{r[1]}</td><td className="center">{r[2]}</td></tr>
            ))}
          </tbody>
        </table>
      </Page>

      {/* 3. Ciri-ciri pemandu */}
      <Page footer={ms()}>
        <h2 className="doc-title">CIRI – CIRI PEMANDU BERHEMAH DAN CEMERLANG</h2>
        <ol className="senarai">{CIRI.map((c, i) => <li key={i}>{c}</li>)}</ol>
      </Page>

      {/* 4. Arahan harian 2.1 */}
      <Page footer={ms()}>
        <h2 className="doc-title">ARAHAN HARIAN KEPADA PEMANDU</h2>
        <p className="bold">2.1&nbsp;&nbsp;Tanggungjawab setiap hari sebelum memandu:</p>
        <ol className="senarai">{SEBELUM.map((c, i) => <li key={i}>{c}</li>)}</ol>
      </Page>

      {/* 5. Arahan 2.2 & 2.3 */}
      <Page footer={ms()}>
        <p className="bold">2.2&nbsp;&nbsp;Tanggungjawab semasa memandu:</p>
        <ol className="senarai">{SEMASA.map((c, i) => <li key={i}>{c}</li>)}</ol>
        <p className="bold" style={{ marginTop: 16 }}>2.3&nbsp;&nbsp;Tanggungjawab selepas tamat perjalanan:</p>
        <ol className="senarai">{SELEPAS.map((c, i) => <li key={i}>{c}</li>)}</ol>
      </Page>

      {/* 6. Kemalangan */}
      <Page footer={ms()}>
        <h2 className="doc-title">TANGGUNGJAWAB SEKIRANYA BERLAKU KEMALANGAN</h2>
        <p style={{ textAlign: "justify", lineHeight: 1.9 }}>
          Apabila berlaku kemalangan, pemandu/ pegawai yang terlibat/ Ketua Jabatan hendaklah membuat laporan polis
          dalam tempoh 24 jam. Bagi Kenderaan Kerajaan, kecuali kereta konsesi, tindakan susulan hendaklah dibuat
          mengikut WP 4.2/2013 – Kemalangan Yang Melibatkan Kenderaan Kerajaan Malaysia dan pindaannya. Bagi kereta
          konsesi, laporan hendaklah dibuat kepada pihak SPANCO serta-merta.
        </p>
      </Page>

      {/* 7. Maklumat kenderaan */}
      <Page footer={ms()}>
        <h2 className="doc-title">MAKLUMAT KENDERAAN</h2>
        <p style={{ textAlign: "center", marginTop: -8, fontStyle: "italic" }}>(Diisi oleh Pegawai Kenderaan)</p>
        <div style={{ display: "flex", gap: 12 }}>
          <table className="grid-table" style={{ width: "46%" }}>
            <thead><tr><th colSpan={2}>Butir-Butir Kenderaan</th></tr></thead>
            <tbody>
              {[
                ["Jenis dan Model Kenderaan:", `${v.make} ${v.model}`],
                ["No. Pendaftaran:", v.registration_number],
                ["No. Chasis:", ""],
                ["No. Enjin:", ""],
                ["Tarikh Pendaftaran:", ""],
                ["Jenis Bahan Api: Petrol/ Diesel/ Gas*", v.fuel_type],
                ["Kapasiti Tangki Bahan Api:", ""],
                ["Kelengkapan Tambahan:", ""],
              ].map((r, i) => (
                <tr key={i}><td style={{ width: "55%" }}>{i + 1}. {r[0]}</td><td className="bold">{r[1]}</td></tr>
              ))}
            </tbody>
          </table>
          <table className="grid-table" style={{ width: "54%" }}>
            <thead>
              <tr><th rowSpan={2} style={{ width: "40%" }}>Item</th><th colSpan={3}>Maklumat Alat Ganti</th></tr>
              <tr><th>Jenis Disyorkan Oleh Pengeluar Kenderaan</th><th>Jangkamasa Diganti</th><th>Kuantiti</th></tr>
            </thead>
            <tbody>
              {ITEM_GANTI.map((it, i) => (
                <tr key={i}><td>{i + 1}. {it}</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="nota">* Potong mana yang tidak berkenaan</div>
      </Page>

      {/* 8. Rekod penyenggaraan */}
      <Page footer={ms()}>
        <h2 className="doc-title">REKOD PENYENGGARAAN BERJADUAL</h2>
        <table className="grid-table">
          <thead>
            <tr>
              <th rowSpan={2} style={{ width: "12%" }}>Tarikh</th>
              <th rowSpan={2} style={{ width: "13%" }}>Bacaan Odometer (Km)</th>
              <th rowSpan={2}>Butir-butir penyenggaraan dan pemeriksaan berkala secara ringkas</th>
              <th rowSpan={2} style={{ width: "18%" }}>Nama/ Tandatangan Pegawai Yang Mengesahkan</th>
              <th colSpan={2} style={{ width: "22%" }}>Jadual penyenggaraan seterusnya</th>
            </tr>
            <tr><th>Tarikh</th><th>* Odometer (Km)</th></tr>
          </thead>
          <tbody>
            {Array.from({ length: 12 }).map((_, i) => (
              <tr key={i}>{Array.from({ length: 6 }).map((__, j) => <td key={j} style={{ height: 30 }}>&nbsp;</td>)}</tr>
            ))}
          </tbody>
        </table>
        <div className="nota">* Bagi kenderaan berat, Ketua Jabatan hendaklah membuat penyesuaian.</div>
      </Page>

      {/* 9. Notis kesalahan */}
      <Page footer={ms()}>
        <h2 className="doc-title">MAKLUMAT MENGENAI NOTIS KESALAHAN LALU LINTAS</h2>
        <table className="grid-table">
          <thead>
            <tr>
              <th style={{ width: "10%" }}>Tarikh</th>
              <th style={{ width: "15%" }}>No. Rujukan Notis Kesalahan</th>
              <th style={{ width: "12%" }}>Amaun Kompaun (RM)</th>
              <th style={{ width: "17%" }}>Pihak Berkuasa Yang Mengeluarkan Notis</th>
              <th>Jenis Dan Maklumat Kesalahan</th>
              <th style={{ width: "14%" }}>Nama Pemandu</th>
              <th style={{ width: "12%" }}>Status Penyelesaian</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 12 }).map((_, i) => (
              <tr key={i}>{Array.from({ length: 7 }).map((__, j) => <td key={j} style={{ height: 30 }}>&nbsp;</td>)}</tr>
            ))}
          </tbody>
        </table>
      </Page>

      {/* 10+. Butir-butir penggunaan */}
      {butirPages.map((p, i) => (
        <ButirPage key={i} data={data} month={p.month} rows={p.rows} isLast={p.isLast} summary={p.summary} footer={ms()} />
      ))}
    </div>
  );
}
